import { http, HttpResponse, delay } from 'msw';
import { GRAPH_ENDPOINTS } from '../constants';
import { MOCK_GRAPH_KEYS } from './graphs.mock';
import type { GraphImage, GraphKey } from '../models';

const graphKeys: GraphKey[] = MOCK_GRAPH_KEYS.map(item => ({ ...item, graph: item.graph ? { ...item.graph } : null }));

function toEnvelope<T>(data: T) {
  return {
    status: true,
    status_code: 200,
    message: 'OK',
    data,
    error: null,
  };
}

function toApiGraph(graph: GraphImage) {
  return {
    id: graph.id,
    graph_key: graph.graphKey,
    original_filename: graph.originalFilename,
    content_type: graph.contentType,
    is_active: graph.isActive,
    uploaded_at: graph.uploadedAt,
  };
}

function toApiKeys(keys: GraphKey[]) {
  return keys.map(item => ({
    key: item.key,
    label: item.label,
    description: item.description,
    graph: item.graph ? toApiGraph(item.graph) : null,
  }));
}

export const graphsHandlers = [
  http.get(GRAPH_ENDPOINTS.KEYS, async ({ request }) => {
    await delay(400);
    const url = new URL(request.url);
    if (url.searchParams.get('error') === '1') {
      return HttpResponse.json({ message: 'Failed to load graph keys' }, { status: 500 });
    }

    return HttpResponse.json(toEnvelope({ keys: toApiKeys(graphKeys) }));
  }),

  http.post('*/admin/graphs/:graphKey', async ({ params, request }) => {
    await delay(600);
    const graphKey = String(params.graphKey ?? '');
    const formData = await request.formData();
    const file = formData.get('file') as File | null;

    if (!file) {
      return HttpResponse.json({ message: 'file is required' }, { status: 400 });
    }

    const index = graphKeys.findIndex(item => item.key === graphKey);
    if (index < 0) {
      return HttpResponse.json({ message: 'Graph key not found' }, { status: 404 });
    }

    const nextGraph: GraphImage = {
      id: `graph-${Date.now()}`,
      graphKey,
      originalFilename: file.name,
      contentType: file.type || 'image/png',
      isActive: true,
      uploadedAt: new Date().toISOString(),
    };

    graphKeys[index] = {
      ...graphKeys[index],
      graph: nextGraph,
    };

    return HttpResponse.json(toEnvelope(toApiGraph(nextGraph)));
  }),
];
